import { useState, useContext } from 'react';
import { DomainContext } from '@/context/DomainContext';
import { addPatientMannually, UpdatePatient, uploadFile } from "@/api/endpoints";
import { GetBrokerPatientById } from '@/api/endpoints/Broker/BrokerPatient';
import { LoadListDefault, useGet } from '@/api/hooks';
import { successToast } from "@/utils/toasters";
import useFormData from '@/hooks/useFormData';

export const yesNoChoices = [
    { label: "yes", value: true },
    { label: "no", value: false }
];

export const genderChoices = [
    { label: "male", value: 0 },
    { label: "female", value: 1 }
];

export const MaritalStatusChoices = [
    { label: "single", value: 0 },
    { label: "married", value: 1 }
];


export const useAddPatient = ({ modalId, patientId, selectedPatient, onReset }) => {
    const [loading, setLoading] = useState(false);
    const [files, setFiles] = useState({});
    const { ToggleReloadTable } = useContext(DomainContext);
    const isEditing = !!patientId;
    const {getFormData} = useFormData();
    const { result: patientData, isPending } = useGet({
        ...LoadListDefault,
        endPoint: isEditing ? GetBrokerPatientById : null,
        params: [selectedPatient?.patientId],
        allowFetch: isEditing && !!selectedPatient?.patientId,
        depArr: [patientId,selectedPatient],
        defaultValue: null
    });

    const handleClose = () => {
        onReset(); 
        setFiles({}); 
        if (modalId?.current) {
            modalId.current.click(); 
        }
    };

    const handleSubmit = async (data) => {
        setLoading(true);
        try {
            const payload = {
                groupName: data?.groupName || "",
                firstName: data?.firstName || "",
                lastName: data?.lastName || "",
                gender: data?.gender,
                birthDate: data?.birthDate,
                nationalityId: Number(data?.nationalityId ?? data?.nationality?.value),
                countryId: Number(data?.countryId ?? data?.country?.value),
                passportNumber: data?.passportNumber || "",
                email: data?.email || "",
                phoneNumber: data?.phoneNumber || "",
                medicalDiagnosis: data?.medicalDiagnosis || "",
                serviceCategoryId: Number(data?.serviceCategory ?? data?.service?.value),
                serviceSubCategoryId: Number(data?.serviceSubCategory ?? data?.["sub-service-category"]?.value),
                treatmentStartDate: data?.treatmentStartDate,
                treatmentEndDate: data?.treatmentEndDate,
                requiresVisa: !!data?.requiresVisa,
                requiresAccommodation: !!data?.requiresAccommodation,
                requiresFlight: !!data?.requiresFlight,
                attachmentIds: [] 
            };

            if (isEditing) {
                payload.patientId = Number(selectedPatient?.brokerPatientId || 0);
                payload.height = Number(data?.height ?? 0);
                payload.weight = Number(data?.weight ?? 0);
                payload.maritalStatus = data?.maritalStatus;
                payload.isSmoker = !!data?.isSmoker;
                payload.isDrinkAlcohol = !!data?.isDrinkAlcohol;
                payload.allergy = data?.checkoutAllergy || "";
                payload.regularMedicationName = data?.checkoutMedication || "";
                payload.medicalOperation = data?.checkoutOperation || "";
                payload.familyMedicalHistory = data?.medical_family_history || " ";
            }

            const hasFiles = Object.values(files).some((fileList) => Array.isArray(fileList) && fileList.length);
            if (hasFiles) {
                const fileData = getFormData(files);
                const fileRes = await uploadFile(fileData);
                payload.attachmentIds = Array.isArray(fileRes?.data) ? fileRes.data : [];
            }

            if (isEditing) {
                await UpdatePatient(selectedPatient?.brokerPatientId, payload);
                successToast("patient-updated-successfully");
            } else {
                await addPatientMannually(payload);
                successToast("patient-added-successfully");
            }

            handleClose();
            ToggleReloadTable((p) => !p);
        } catch (err) {
            console.error("Submit Err:", err);
        } finally {
            setLoading(false);
        }
    };

    return {
        loading,
        files,
        setFiles,
        patientData,
        isPending,
        handleSubmit,
        handleClose,
        yesNoChoices,
        genderChoices,
        MaritalStatusChoices,
        isEditing
    };
};
